/**
 * Section 17 — Hotkeys & Shortcuts.
 *
 * Keyboard-driven navigation. Cmd-K command palette, "?" overlay,
 * chart-specific keys, page-jump keys.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import GuideKeycap from '../GuideKeycap';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_hotkeys: GuideSection = {
  id: 'hotkeys',
  label: 'Hotkeys & Shortcuts',
  group: 'help',
  icon: <KeyboardIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Hotkeys & Shortcuts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Keyboard navigation for power users. Press <GuideKeycap>?</GuideKeycap> on any page for the live
        overlay; this reference is comprehensive.
      </Typography>

      <H id="cmd-k">Command palette — <GuideKeycap>⌘ K</GuideKeycap> / <GuideKeycap>Ctrl K</GuideKeycap></H>
      <P>
        The fast way to navigate. Press once, type 1-3 characters, hit Enter. Knows about every page, every
        recently-viewed symbol, the App Guide entry point, and common actions.
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Finds</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>"das"</TableCell><TableCell>Dashboard</TableCell></TableRow>
          <TableRow><TableCell>"set"</TableCell><TableCell>Settings</TableCell></TableRow>
          <TableRow><TableCell>"btc"</TableCell><TableCell>BTCUSD chart (any symbol in your watchlists or recent navigations)</TableCell></TableRow>
          <TableRow><TableCell>"guide"</TableCell><TableCell>Opens App Guide in a new tab</TableCell></TableRow>
          <TableRow><TableCell>"help"</TableCell><TableCell>Same — App Guide</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        Recent picks are remembered (stored in localStorage) so your most-used jumps are at the top of the
        list. Up/Down arrows to navigate, Enter to select, Esc to close.
      </P>

      <H id="page-jump">Page-jump keys (Vim-style <GuideKeycap>g</GuideKeycap>-prefix sequences)</H>
      <P>
        Tap <GuideKeycap>g</GuideKeycap> first, then the second key within ~1 second. The combination is
        modeled on Vim and Gmail's <em>go-to-X</em> bindings — neither key works alone, so a stray
        keystroke on the dashboard can never accidentally navigate you away.
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Sequence</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Page</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>d</GuideKeycap></TableCell><TableCell>Dashboard</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>t</GuideKeycap></TableCell><TableCell>Trades</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>a</GuideKeycap></TableCell><TableCell>Alerts</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>c</GuideKeycap></TableCell><TableCell>Charts (last opened symbol)</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>s</GuideKeycap></TableCell><TableCell>Strategies</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>n</GuideKeycap></TableCell><TableCell>Analytics</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>i</GuideKeycap></TableCell><TableCell>Instruments</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>l</GuideKeycap></TableCell><TableCell>Logs</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>f</GuideKeycap></TableCell><TableCell>FNO Dashboard</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>,</GuideKeycap></TableCell><TableCell>Settings</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>g</GuideKeycap> <GuideKeycap>h</GuideKeycap></TableCell><TableCell>App Guide (opens in new tab)</TableCell></TableRow>
        </TableBody>
      </Table>
      <GuideCallout kind="info">
        Page-jump sequences are disabled when a text input has focus — so typing a search query or
        password into a field doesn't trigger navigation. The hotkey overlay (press <GuideKeycap>?</GuideKeycap>
        on any page) is the live source of truth if a binding ever drifts.
      </GuideCallout>

      <H id="chart-keys">Charts-specific keys</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><GuideKeycap>1</GuideKeycap>–<GuideKeycap>9</GuideKeycap></TableCell><TableCell>Switch the focused pane's interval — 1=1m, 2=3m, 3=5m, 4=15m, 5=30m, 6=1H, 7=4H, 8=1D, 9=1W</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>b</GuideKeycap></TableCell><TableCell>Toggle Bollinger overlay</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>c</GuideKeycap></TableCell><TableCell>Toggle AvgCloud</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>p</GuideKeycap></TableCell><TableCell>Toggle CPR</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>r</GuideKeycap></TableCell><TableCell>Toggle Bar Replay mode</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>n</GuideKeycap> <GuideKeycap>b</GuideKeycap></TableCell><TableCell>Enter nBUY trigger mode (next click on chart sets the level)</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>n</GuideKeycap> <GuideKeycap>s</GuideKeycap></TableCell><TableCell>Enter nSHORT trigger mode</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>Shift B</GuideKeycap> <GuideKeycap>b</GuideKeycap></TableCell><TableCell>Enter bBUY (breakout long) mode</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>Shift B</GuideKeycap> <GuideKeycap>s</GuideKeycap></TableCell><TableCell>Enter bSHORT (breakdown short) mode</TableCell></TableRow>
          <TableRow><TableCell><GuideKeycap>Esc</GuideKeycap></TableCell><TableCell>Cancel any active trigger / drawing mode</TableCell></TableRow>
        </TableBody>
      </Table>

      <H id="why-no-execute">Why there's no "place order" hotkey</H>
      <P>
        Intentional. Every order is a real broker order with real-money consequences. The OrderConfirmDialog
        forces a deliberate click. Hotkeys exist to navigate + set levels — never to fire.
      </P>
    </>
  ),
  searchIndex: [
    { heading: 'Command palette ⌘K',  anchor: 'cmd-k',       body: 'command palette cmd k ctrl k search navigation' },
    { heading: 'Page-jump keys (g-prefix Vim style)', anchor: 'page-jump', body: 'page jump g prefix sequence vim gd gt ga gc gs gn gi gl gf g comma gh dashboard trades alerts charts strategies analytics instruments logs fno settings guide' },
    { heading: 'Charts-specific',    anchor: 'chart-keys',  body: 'charts hotkeys interval bollinger cloud cpr replay nbuy nshort bbuy bshort' },
    { heading: 'No place-order key', anchor: 'why-no-execute', body: 'no place order hotkey safety deliberate' },
  ],
};

export default Section_hotkeys;
